import type { MessageInterface } from '../hooks/useMessageProcessing';
import { extractStreamingMessage, isMessageStreaming } from '../types/parlant';
import type { ButtonVariant } from '../types/theme';

export interface MessageBubbleComparableProps {
  message: MessageInterface;
  displayName: string;
  timestamp: string;
  isStatusMessage?: boolean;
  onButtonClick?: (url: string, label: string, variant?: ButtonVariant) => void;
  isLastAgentMessage?: boolean;
  copyableText?: string;
  copyTooltip?: string;
  isContinual?: boolean;
  onShowLogs?: () => void;
  t?: (key: string, params?: Record<string, string>) => string;
  debugMode?: boolean;
  layoutMode?: 'popup' | 'fullscreen';
}

function getMessageText(message: MessageInterface): string {
  const data = message.data as Record<string, unknown>;
  const streamed = extractStreamingMessage(data);
  if (typeof streamed === 'string' && streamed.length > 0) {
    return streamed;
  }
  const raw = data?.message;
  return typeof raw === 'string' ? raw : '';
}

function messageFieldsEqual(
  prev: MessageInterface,
  next: MessageInterface
): boolean {
  const prevData = prev.data as Record<string, unknown>;
  const nextData = next.data as Record<string, unknown>;

  return (
    prev.id === next.id &&
    prev.offset === next.offset &&
    prev.source === next.source &&
    prev.status === next.status &&
    prev.serverStatus === next.serverStatus &&
    prev.flagged === next.flagged &&
    prev.flaggedReason === next.flaggedReason &&
    prev.draft === next.draft &&
    prev.deleted === next.deleted &&
    prev.error === next.error &&
    prev.isStatusMessage === next.isStatusMessage &&
    prev.displayStatusText === next.displayStatusText &&
    getMessageText(prev) === getMessageText(next) &&
    isMessageStreaming(prevData) === isMessageStreaming(nextData) &&
    prevData?.status === nextData?.status &&
    prevData?._interrupted === nextData?._interrupted
  );
}

/**
 * Custom props comparator for memoized MessageBubble.
 * Compares meaningful message content instead of object identity so completed
 * bubbles skip re-rendering when siblings update during streaming chunk updates.
 */
export function messageBubblePropsEqual(
  prev: MessageBubbleComparableProps,
  next: MessageBubbleComparableProps
): boolean {
  return (
    messageFieldsEqual(prev.message, next.message) &&
    prev.displayName === next.displayName &&
    prev.timestamp === next.timestamp &&
    prev.isStatusMessage === next.isStatusMessage &&
    prev.isLastAgentMessage === next.isLastAgentMessage &&
    prev.copyableText === next.copyableText &&
    prev.copyTooltip === next.copyTooltip &&
    prev.isContinual === next.isContinual &&
    prev.debugMode === next.debugMode &&
    prev.layoutMode === next.layoutMode &&
    prev.onButtonClick === next.onButtonClick &&
    prev.onShowLogs === next.onShowLogs &&
    prev.t === next.t
  );
}
